// src/components/pipelines/nodes/WaitNode.jsx - Wait/Delay Node for React Flow
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Button } from '../../ui/button.tsx';
import { Clock, Edit2, Check, Flag } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

/**
 * WaitNode - Represents a time delay between workflow sections
 *
 * Node data structure:
 * {
 *   id: 'wait-uuid',
 *   type: 'wait',
 *   data: {
 *     duration: 2,
 *     unit: 'days' | 'hours' | 'minutes',
 *     description: 'Wait for customer response'
 *   }
 * }
 */
const WaitNode = ({ data, selected, id }) => {
  const {
    duration = 1,
    unit = 'days',
    description,
    isStageGate = false,
    stageName = '',
    stageColor = 'yellow'
  } = data;
  const { updateNodeData } = useReactFlow();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');

  // Format duration display
  const getDurationText = () => {
    const unitLabel = duration === 1 ? unit.slice(0, -1) : unit;
    return `${duration} ${unitLabel}`;
  };

  // Update duration
  const handleDurationChange = (newDuration) => {
    const value = parseInt(newDuration) || 1;
    updateNodeData(id, { ...data, duration: value });
  };

  // Update unit
  const handleUnitChange = (newUnit) => {
    updateNodeData(id, { ...data, unit: newUnit });
  };

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  // Cancel editing description
  const handleCancelDescription = () => {
    setEditedDescription(description || '');
    setIsEditingDescription(false);
  };

  return (
    <div className="wait-node relative">
      {/* Stage gate indicator flag */}
      {isStageGate && (
        <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shadow-lg z-10 border-2 border-slate-950">
          <Flag className="w-3.5 h-3.5 text-white" />
        </div>
      )}

      {/* Input handle (left side) */}
      <Handle isConnectable={1}
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-yellow-500"
      />

      <Card className={`
        w-48
        ${selected ? 'ring-2 ring-yellow-500 shadow-lg' : 'shadow-md'}
        transition-all duration-200
        hover:shadow-xl
        bg-gradient-to-br from-yellow-500/10 to-yellow-600/5
        border-yellow-500/20
      `}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-yellow-100 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Wait
            </CardTitle>
            <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-300">
              Delay
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="pt-0 space-y-2">
          {/* Editable Duration */}
          <div className="text-center">
            <Input
              type="number"
              min="1"
              value={duration}
              onChange={(e) => handleDurationChange(e.target.value)}
              className="h-12 text-2xl font-bold text-center bg-yellow-500/10 border-yellow-500/30 text-yellow-200 nodrag"
            />
          </div>

          {/* Editable Unit */}
          <Select value={unit} onValueChange={handleUnitChange}>
            <SelectTrigger className="w-full bg-yellow-500/10 border-yellow-500/30 text-yellow-400 text-sm nodrag">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="minutes" className="text-slate-200">Minutes</SelectItem>
              <SelectItem value="hours" className="text-slate-200">Hours</SelectItem>
              <SelectItem value="days" className="text-slate-200">Days</SelectItem>
              <SelectItem value="weeks" className="text-slate-200">Weeks</SelectItem>
            </SelectContent>
          </Select>

          {/* Editable Description */}
          <div className="border-t border-yellow-500/10 pt-2">
            {isEditingDescription ? (
              <div className="space-y-2 nodrag">
                <Input
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onBlur={handleSaveDescription}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveDescription();
                    if (e.key === 'Escape') handleCancelDescription();
                  }}
                  placeholder="Add description..."
                  autoFocus
                  className="text-xs bg-slate-800 border-slate-700 text-slate-200"
                />
              </div>
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic text-center">
                    {description}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Add description...
                  </div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-yellow-400" />
              </div>
            )}
          </div>

          {/* Stage Gate Toggle Section */}
          <div className="border-t border-yellow-500/10 pt-3">
            <label className="flex items-center gap-2 cursor-pointer nodrag">
              <input
                type="checkbox"
                checked={isStageGate}
                onChange={(e) => updateNodeData(id, {
                  ...data,
                  isStageGate: e.target.checked,
                  stageName: e.target.checked && !stageName ? 'New Stage' : stageName
                })}
                className="w-4 h-4 text-purple-500 bg-slate-800 border-slate-600 rounded focus:ring-purple-500"
              />
              <span className="text-xs text-slate-300 font-medium">Make this a stage gate</span>
            </label>

            {isStageGate && (
              <div className="mt-2 space-y-2 nodrag">
                <Input
                  placeholder="Stage name (e.g., 'Waiting Period')"
                  value={stageName}
                  onChange={(e) => updateNodeData(id, {
                    ...data,
                    stageName: e.target.value
                  })}
                  className="text-xs bg-slate-800/50 border-slate-700 text-slate-200"
                />
                <Select
                  value={stageColor}
                  onValueChange={(color) => updateNodeData(id, {
                    ...data,
                    stageColor: color
                  })}
                >
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="purple" className="text-slate-200">Purple</SelectItem>
                    <SelectItem value="blue" className="text-slate-200">Blue</SelectItem>
                    <SelectItem value="green" className="text-slate-200">Green</SelectItem>
                    <SelectItem value="orange" className="text-slate-200">Orange</SelectItem>
                    <SelectItem value="red" className="text-slate-200">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Output handle (right side) */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-yellow-500"
      />
    </div>
  );
};

export default WaitNode;
